import { Dayjs } from 'dayjs'
import { SharedSlice } from 'src/components/slice'
import { StateCreator } from 'zustand'
import { IStockReceived, ISupplierStatement } from '$models'
import { stockReceivedService, supplierStatementService } from '$services'
import orderBy from 'lodash/orderBy'
import { enqueueLoadedSnackbar, exceptionMessageHandler, logger } from '$utils'
import { StockReceivedRow } from '../_common/helpers/stockReceivedRow'
import { ReportsSlice } from '../slice'
import { InvoiceDateProperty } from '$constants'
import { isCancelledError } from '@tanstack/react-query'

interface SupplierStatementsState {
  isUpdatingPeriodRange: boolean
  stockReceivedRows: StockReceivedRow[]
}

export interface SupplierStatementsSlice extends SupplierStatementsState {
  resetStatementsSlice: () => void

  getStockReceivedRows: (
    periodStart: Dayjs,
    periodEnd: Dayjs,
    supplierId: string,
    includeAdjustingInvoices: boolean,
    statementId?: string
  ) => Promise<StockReceivedRow[]>

  updateStockReceivedReconciled(
    documentId: string,
    reconciled: boolean
  ): StockReceivedRow[]

  /**
   * Delete supplier statement
   * @param supplierStatement - to update
   * @param successMessage Success Message to display if the delete was successful
   * @param errorMessage Error Message to display if the delete has failed
   */
  deleteSupplierStatement(
    supplierStatement: ISupplierStatement,
    successMessage?: string,
    errorMessage?: string
  ): Promise<void>

  /**
   * Update supplier statement
   * @param supplierStatement - to update
   * returns ISupplierStatement
   */
  updateSupplierStatement(
    supplierStatement: ISupplierStatement
  ): Promise<ISupplierStatement | null>
}

const initialState: SupplierStatementsState = {
  isUpdatingPeriodRange: false,
  stockReceivedRows: [],
}

const createSupplierStatementsSlice: StateCreator<
  SupplierStatementsSlice & SharedSlice & ReportsSlice,
  [],
  [],
  SupplierStatementsSlice
> = (set, get) => ({
  ...initialState,

  resetStatementsSlice: () => set(initialState),

  getStockReceivedRows: async (
    periodStart: Dayjs,
    periodEnd: Dayjs,
    supplierId: string,
    includeAdjustingInvoices: boolean,
    statementId?: string
  ): Promise<StockReceivedRow[]> => {
    try {
      set(() => ({ isUpdatingPeriodRange: true }))

      const ordersInRangePromise: Promise<IStockReceived[]> =
        stockReceivedService.getStockReceivedItems(
          periodStart,
          periodEnd,
          InvoiceDateProperty.Invoiced,
          supplierId,
          includeAdjustingInvoices
        )

      const ordersBeforeRangePromise: Promise<IStockReceived[]> =
        stockReceivedService.getStockReceivedItemsByReferenceDate(
          periodStart,
          supplierId,
          2,
          true
        )

      const ordersAfterRangePromise: Promise<IStockReceived[]> =
        stockReceivedService.getStockReceivedItemsByReferenceDate(
          periodEnd,
          supplierId,
          2,
          false
        )

      const [ordersInRange, ordersBeforeRange, ordersAfterRange] =
        await Promise.all([
          ordersInRangePromise,
          ordersBeforeRangePromise,
          ordersAfterRangePromise,
        ])

      const sortedOrdersInRange: IStockReceived[] = orderBy(
        ordersBeforeRange.concat(ordersInRange).concat(ordersAfterRange),
        'invoicedAt',
        'asc'
      )

      const stockReceivedIdsInRange = ordersInRange.map((order) => order.id!)

      const updatedStockReceives: StockReceivedRow[] = sortedOrdersInRange.map(
        (stockReceive) => {
          const isInRange = stockReceivedIdsInRange.includes(stockReceive.id!)

          if (isInRange) {
            return {
              ...stockReceive,
              isDisabled: false,
              isEditable: true,
              isWithinPeriod: true,
              isReconciledToAnotherStatement:
                stockReceive.reconciled &&
                stockReceive.statementId !== statementId,
            }
          } else {
            return {
              ...stockReceive,
              isDisabled: true,
              isEditable: false,
              isWithinPeriod: false,
            }
          }
        }
      )

      set(() => ({
        stockReceivedRows: updatedStockReceives,
      }))

      return updatedStockReceives
    } catch (error) {
      if (isCancelledError(error)) {
        logger.debug('Stock Received Items query was cancelled')
        // We do a return here as we don't want to update the state if the query was cancelled,
        // otherwise it would be an empty array until the last call finishes and return the values
        return get().stockReceivedRows
      }

      exceptionMessageHandler(error, 'Failed to retrieve stock received items')
      return []
    } finally {
      set(() => ({ isUpdatingPeriodRange: false }))
    }
  },

  updateStockReceivedReconciled(
    documentId: string,
    reconciled: boolean
  ): StockReceivedRow[] {
    const updatedStockReceivedRows = get().stockReceivedRows.map(
      (stockReceive) => {
        if (stockReceive.id === documentId) {
          return { ...stockReceive, reconciled: reconciled }
        } else {
          return stockReceive
        }
      }
    )

    set(() => ({
      stockReceivedRows: updatedStockReceivedRows,
    }))

    return updatedStockReceivedRows
  },

  deleteSupplierStatement: async (
    supplierStatement: ISupplierStatement,
    successMessage?: string,
    errorMessage?: string
  ): Promise<void> => {
    try {
      await supplierStatementService.deleteSupplierStatement(
        supplierStatement.id!
      )

      set((prevState) => ({
        supplierStatements: prevState.supplierStatements.filter(
          (statement) => statement.id !== supplierStatement.id
        ),
      }))

      enqueueLoadedSnackbar(successMessage, 'success')
    } catch (error) {
      exceptionMessageHandler(
        error,
        errorMessage ?? 'Failed to delete the supplier statement'
      )
    }
  },

  updateSupplierStatement: async (
    supplierStatement: ISupplierStatement
  ): Promise<ISupplierStatement | null> => {
    try {
      const updatedSupplierStatement: ISupplierStatement =
        await supplierStatementService.updateSupplierStatement(
          supplierStatement.id!,
          supplierStatement
        )

      set((prevState) => {
        // Find the index of the existing supplier statement
        const index = prevState.supplierStatements.findIndex(
          (statement) => statement.id === updatedSupplierStatement.id
        )

        // If found, replace it with the updated supplier statement
        if (index !== -1) {
          const newStatements = [...prevState.supplierStatements]
          newStatements[index] = updatedSupplierStatement // Replace with updated statement
          return { supplierStatements: newStatements } // Return the new state
        }

        // If not found, return the previous state
        return prevState
      })

      return updatedSupplierStatement
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to update the supplier statement')
      return null
    }
  },
})

export default createSupplierStatementsSlice
